/**
 * useAvailableSkillsWithStatus — Fetch ALL available skills + installed status
 * 
 * - GET /machines/:id/available-skills (all skills_metadata + installed flag)
 * - Auto-refetch every 30s
 * - Search/filter by category
 * - Install skill via POST /machines/:id/install-skill
 */

import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { api } from '../api/client';

export interface AvailableSkill {
  id: string;
  name: string;
  description: string;
  category: string;
  emoji: string;
  version: string;
  author: string;
  tags: string[];
  installed: boolean;
  isFavorite?: boolean;
  tier?: string;
  badge?: string;
  description_pt?: string;
  requires_api_key?: boolean;
}

export interface UseAvailableSkillsResult {
  skills: AvailableSkill[];
  loading: boolean;
  error: string;
  installedCount: number;
  availableCount: number;
  installing: string | null;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  categoryFilter: string;
  setCategoryFilter: (c: string) => void;
  categories: string[];
  filteredSkills: AvailableSkill[];
  refetch: () => Promise<void>;
  installSkill: (skillName: string) => Promise<boolean>;
  uninstallSkill: (skillName: string) => Promise<boolean>;
  uninstalling: string | null;
}

const REFETCH_INTERVAL = 30_000;

/**
 * Detect resource type from ID prefix.
 * Runner IDs start with "runner_v3_" or "runner_"; everything else is a cloud machine.
 */
function getBasePath(id: string): string {
  if (id.startsWith('runner_v3_') || id.startsWith('runner_')) return '/runners';
  return '/machines';
}

export function useAvailableSkillsWithStatus(machineId: string): UseAvailableSkillsResult {
  const [skills, setSkills] = useState<AvailableSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [installedCount, setInstalledCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [installing, setInstalling] = useState<string | null>(null);
  const [uninstalling, setUninstalling] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSkills = useCallback(async () => {
    try {
      const [res, manifest] = await Promise.all([
        api.get<{
          data: {
            machineId: string;
            availableSkills: AvailableSkill[];
            installedCount: number;
            availableCount: number;
          };
        }>(`${getBasePath(machineId)}/${machineId}/available-skills`),
        api.get<any>('/cloud/skills-pack/manifest').catch(() => null),
      ]);
      const data = (res as any)?.data || res;
      const rawSkills: AvailableSkill[] = data.availableSkills || [];

      // Build manifest lookup
      const metaMap = new Map<string, { tier?: string; badge?: string; description_pt?: string; requires_api_key?: boolean }>();
      const mSkills = (manifest as any)?.skills || (manifest as any)?.data?.skills || [];
      for (const s of mSkills) {
        metaMap.set(s.name, {
          tier: s.tier || 'tier1',
          badge: s.badge || 'verified',
          description_pt: s.description_pt,
          requires_api_key: !!s.requires_api_key,
        });
      }

      // Enrich skills
      const enriched = rawSkills.map(skill => {
        const m = metaMap.get(skill.name);
        return m ? { ...skill, tier: m.tier, badge: m.badge, description_pt: m.description_pt, requires_api_key: m.requires_api_key } : skill;
      });

      setSkills(enriched);
      setInstalledCount(data.installedCount || 0);
      setAvailableCount(data.availableCount || 0);
      setError('');
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar skills disponíveis');
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  const installSkill = useCallback(async (skillName: string): Promise<boolean> => {
    setInstalling(skillName);
    try {
      await api.post(`${getBasePath(machineId)}/${machineId}/install-skill`, { skillName });
      // Optimistic update
      setSkills(prev => prev.map(s =>
        s.name === skillName ? { ...s, installed: true } : s
      ));
      setInstalledCount(prev => prev + 1);
      return true;
    } catch (e: any) {
      setError(e.message || 'Erro ao instalar skill');
      return false;
    } finally {
      setInstalling(null);
    }
  }, [machineId]);

  const uninstallSkill = useCallback(async (skillName: string): Promise<boolean> => {
    setUninstalling(skillName);
    try {
      await api.delete(`${getBasePath(machineId)}/${machineId}/uninstall-skill?skillName=${encodeURIComponent(skillName)}`);
      setSkills(prev => prev.map(s =>
        s.name === skillName ? { ...s, installed: false } : s
      ));
      setInstalledCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (e: any) {
      setError(e.message || 'Erro ao desinstalar skill');
      return false;
    } finally {
      setUninstalling(null);
    }
  }, [machineId]);

  // Derived: categories
  const categories = Array.from(new Set(skills.map(s => s.category || 'general'))).sort();

  // Derived: filtered skills
  const filteredSkills = skills.filter(s => {
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        (s.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  useEffect(() => {
    setLoading(true);
    fetchSkills();
  }, [machineId, fetchSkills]);

  useEffect(() => {
    intervalRef.current = setInterval(fetchSkills, REFETCH_INTERVAL);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchSkills]);

  return {
    skills,
    loading,
    error,
    installedCount,
    availableCount,
    installing,
    searchQuery,
    setSearchQuery,
    categoryFilter,
    setCategoryFilter,
    categories,
    filteredSkills,
    refetch: fetchSkills,
    installSkill,
    uninstallSkill,
    uninstalling,
  };
}
